import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/useAuth';
import { useI18n } from '../context/useI18n';
import {
  isoWeekKey,
  shiftIsoWeekKey,
  WEEKLY_PROGRAM_FIELDS,
  resolveExerciseForWeek,
  computeActiveWeekStreak,
} from '../utils/trainingWeekOverrides';
import SportDayPrescriptionEditor from './training/SportDayPrescriptionEditor';
import RunningPlanWizard from './training/RunningPlanWizard';
import CoachRoutinesTab, { emptyCoachTemplatePlan } from './training/CoachRoutinesTab';
import { emptySportPrescription, normalizeSessionKind, SESSION_KINDS } from '../utils/trainingSportSession';
import { appendGeneratedDays } from '../utils/runningPlanGenerator';
import { WEEKDAYS, nextWeekday, weekdayLabel } from '../utils/trainingWeekday';
import { latestLogPerUser } from '../utils/trainingDiaryLogs';
import './AdminTrainingManager.css';

const LS_TAB = 'training.admin.tab';
const LS_PLANS_SEARCH = 'training.admin.plansSearch';
const LS_GALLERY_SEARCH = 'training.admin.gallerySearch';
const SAVE_DEBOUNCE_MS = 700;

function isTemplatePlanId(planId) {
    return String(planId || '').startsWith('tpl:');
}

const PLAN_LEVEL_LABELS = {
  principiante: 'Principiante',
  intermedio: 'Intermedio',
  avanzado: 'Avanzado',
};

function formatPlanLevel(level) {
  const key = String(level || '').trim().toLowerCase();
  if (PLAN_LEVEL_LABELS[key]) return PLAN_LEVEL_LABELS[key];
  if (!key) return '—';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function isD28dMethod(method) {
  return /^d28d$/i.test(String(method || '').trim());
}

function formatPlanMethod(method, { hidePlatformBranding = false } = {}) {
  const raw = String(method || '').trim();
  if (!raw) return '';
  if (hidePlatformBranding && isD28dMethod(raw)) return '';
  return raw;
}

function formatPlanLevelCell(plan, { coachView = false } = {}) {
  const level = formatPlanLevel(plan?.level);
  if (coachView) return level;
  const method = formatPlanMethod(plan?.method, { hidePlatformBranding: false });
  return method ? `${level} / ${method}` : level;
}

function formatPlanEditorMeta(plan, { coachView = false } = {}) {
  const level = formatPlanLevel(plan?.level);
  if (coachView) return level;
  const method = formatPlanMethod(plan?.method, { hidePlatformBranding: false });
  return method ? `${level} · ${method}` : level;
}

function patchPlan(url, body) {
    return api.patch(url, body, { skipAuthClearOn401: true });
}

/** Carga por serie (igual que Reps: "35 / 40 / 40"); fallback a weight_kg escalar. */
function formatDiaryLoad(ex) {
    const done = String(ex?.weights_done || '').trim();
    if (done) return done;
    const sw = ex?.set_weights || ex?.setWeights;
    if (sw && typeof sw === 'object' && !Array.isArray(sw)) {
        const vals = Object.keys(sw)
            .sort((a, b) => Number(a) - Number(b))
            .map((k) => sw[k])
            .filter((v) => v !== '' && v != null);
        if (vals.length) return vals.join(' / ');
    }
    if (Array.isArray(sw) && sw.length) {
        const vals = sw.filter((v) => v !== '' && v != null);
        if (vals.length) return vals.join(' / ');
    }
    if (ex?.weight_kg != null && ex.weight_kg !== '') return String(ex.weight_kg);
    return '—';
}

function DiaryLogCard({ log }) {
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm">
            <div className="flex justify-between items-center mb-3">
                <div>
                    <span className="font-bold text-lg">{log.user_name}</span>
                    <span className="text-stone-500 text-sm ml-2">({log.fecha})</span>
                </div>
                {log.completado ? (
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded-full">Completado ({log.duration_minutes} min)</span>
                ) : (
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full">En Progreso</span>
                )}
            </div>
            <p className="text-sm font-semibold text-stone-700 bg-stone-100 px-3 py-1 rounded inline-block mb-3">Día de Rutina: {log.dia}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {log.ejercicios?.map((ex, i) => {
                    const loadLabel = formatDiaryLoad(ex);
                    return (
                    <div key={i} className="border border-stone-100 rounded-lg p-3 bg-stone-50 text-sm">
                        <p className="font-bold mb-1 truncate">{ex.exercise_name}</p>
                        <p className="text-stone-600">Series completadas: <span className="font-bold">{ex.sets_done}</span></p>
                        <p className="text-stone-600">Reps: <span className="font-bold">{ex.reps_done}</span></p>
                        <p className="text-stone-600">Carga: <span className="font-bold">{loadLabel === '—' ? '—' : `${loadLabel} kg`}</span></p>
                        <p className="text-stone-600">Intensidad real: <span className="font-bold">{ex.intensity_actual}</span></p>
                        {ex.notes && (
                            <p className="text-amber-700 mt-1 italic text-xs">
                                &quot; {ex.notes} &quot;
                            </p>
                        )}
                    </div>
                    );
                })}
            </div>
            {log.wellness && (
                <div className="mt-3 p-3 bg-lime-50 border border-lime-100 rounded-xl flex flex-wrap gap-4 text-xs font-semibold text-lime-900">
                    <div className="flex items-center gap-1">😴 {log.wellness.sleep_hours}h (Calidad: {log.wellness.sleep_quality}/10)</div>
                    <div className="flex items-center gap-1">⚡ Energía: {log.wellness.energy_level}/10</div>
                    <div className="flex items-center gap-1">😰 Estrés: {log.wellness.stress_level}/10</div>
                    <div className="flex items-center gap-1">🍴 Apetito: {log.wellness.appetite}/10</div>
                </div>
            )}
            <div className="mt-4 pt-3 border-t border-stone-100">
                <label className="text-xs font-bold text-stone-500 block mb-1">NOTAS DEL ENTRENADOR</label>
                <textarea
                    className="w-full input text-sm mb-2 h-16"
                    placeholder="Escribe un comentario sobre esta sesión..."
                    defaultValue={log.trainer_notes}
                    onBlur={(e) => api.put(`/training/admin/log/${log.id}`, { trainer_notes: e.target.value })}
                />
                <p className="text-xs text-stone-400">Los cambios se guardan automáticamente al salir del recuadro.</p>
            </div>
        </div>
    );
}

export default function AdminTrainingManager({ onBack = null, onNavigate = null }) {
    const { user } = useAuth();
    const { t } = useI18n();
    const isCoachOnlyView = useMemo(() => {
        const roles = Array.isArray(user?.roles) && user.roles.length
            ? user.roles
            : [user?.rol].filter(Boolean);
        const isCoach = roles.includes('entrenador');
        const isPlatformAdmin = roles.some((r) => [
            'super_admin', 'admin_d28d', 'admin_training', 'admin_entrenador',
            'admin_marca', 'admin_gimnasio', 'admin_gym',
        ].includes(r));
        return isCoach && !isPlatformAdmin;
    }, [user?.roles, user?.rol]);

    const [activeTab, setActiveTab] = useState(() => {
        try {
            const raw = localStorage.getItem(LS_TAB) || 'routines';
            // «Editor de Rutina» vacío eliminado: redirigir a biblioteca.
            if (raw === 'editor') return 'routines';
            return raw;
        } catch {
            return 'routines';
        }
    });
    const [plans, setPlans] = useState([]);
    const [logs, setLogs] = useState([]);
    const [diaryHistoryOpen, setDiaryHistoryOpen] = useState({});
    const diaryByUser = useMemo(() => latestLogPerUser(logs), [logs]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [libraryRefreshKey, setLibraryRefreshKey] = useState(0);
    const [templateSaving, setTemplateSaving] = useState(false);
    const [templateAssignUserId, setTemplateAssignUserId] = useState('');
    const [templateClients, setTemplateClients] = useState([]);
    const [plansSearch, setPlansSearch] = useState(() => {
        try { return localStorage.getItem(LS_PLANS_SEARCH) || ''; } catch { return ''; }
    });

    // Editor states
    const [editingPlan, setEditingPlan] = useState(null);
    /** base = plantilla permanente; week = override ISO (ejercicio/series/reps/intensidad) */
    const [programScope, setProgramScope] = useState('base');
    const [programWeek, setProgramWeek] = useState(() => isoWeekKey());
    const [showGenerator, setShowGenerator] = useState(false);
    const [previewDayIdx, setPreviewDayIdx] = useState(null);
    const [galleryExercises, setGalleryExercises] = useState([]);
    const [gallerySearch, setGallerySearch] = useState(() => {
        try { return localStorage.getItem(LS_GALLERY_SEARCH) || ''; } catch { return ''; }
    });
    const [pickerDayIdx, setPickerDayIdx] = useState(null);
    const [duplicating, setDuplicating] = useState(false);
    const [sportAssigningDay, setSportAssigningDay] = useState(null);
    const [sportAssignMsg, setSportAssignMsg] = useState({ dayIdx: null, text: '' });
    const [showRunningWizard, setShowRunningWizard] = useState(false);
    const [applyingRunningPlan, setApplyingRunningPlan] = useState(false);
    const saveTimersRef = useRef({});
    const pendingSavesRef = useRef({});
    const daySaveChainRef = useRef(Promise.resolve());

    const clearSaveTimer = useCallback((key) => {
        if (saveTimersRef.current[key]) {
            clearTimeout(saveTimersRef.current[key]);
            delete saveTimersRef.current[key];
        }
    }, []);

    const reportSaveError = useCallback((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        console.error(err);
        const status = err?.response?.status;
        const msg = err?.response?.data?.error;
        if (status === 401 || status === 403) {
            alert(msg || 'Sin permiso o sesión expirada. Copia tus cambios y vuelve a iniciar sesión.');
            return;
        }
        if (!err?.response) {
            alert('El guardado se cortó. Espera un segundo y vuelve a pegar el enlace o pulsa Asignar entreno.');
            return;
        }
        alert(msg || 'Error guardando cambios');
    }, []);

    const persistExerciseField = useCallback(async (planId, dayIdx, exIdx, field, value, week = null) => {
        try {
            const body = { [field]: value };
            if (week) body.week = week;
            await patchPlan(
                `/training/admin/plans/${planId}/exercise/${dayIdx}/${exIdx}`,
                body,
            );
        } catch (err) {
            reportSaveError(err);
        }
    }, [reportSaveError]);

    const persistDayField = useCallback(async (planId, dayIdx, bodyData) => {
        const run = async () => {
            try {
                await patchPlan(`/training/admin/plans/${planId}/day/${dayIdx}`, bodyData);
            } catch (err) {
                reportSaveError(err);
            }
        };
        const next = daySaveChainRef.current.then(run, run);
        daySaveChainRef.current = next.catch(() => {});
        await next;
    }, [reportSaveError]);

    const flushPendingSaves = useCallback(() => {
        const pending = { ...pendingSavesRef.current };
        pendingSavesRef.current = {};
        Object.keys(pending).forEach((key) => clearSaveTimer(key));
        Object.values(pending).forEach((item) => {
            if (item?.kind === 'exercise') {
                void persistExerciseField(item.planId, item.dayIdx, item.exIdx, item.field, item.value, item.week || null);
            } else if (item?.kind === 'day') {
                void persistDayField(item.planId, item.dayIdx, item.bodyData);
            }
        });
    }, [clearSaveTimer, persistDayField, persistExerciseField]);

    const queueExerciseSave = useCallback((planId, dayIdx, exIdx, field, value, week = null) => {
        if (isTemplatePlanId(planId)) return;
        const key = `ex-${planId}-${dayIdx}-${exIdx}-${field}-${week || 'base'}`;
        clearSaveTimer(key);
        pendingSavesRef.current[key] = { kind: 'exercise', planId, dayIdx, exIdx, field, value, week };
        saveTimersRef.current[key] = setTimeout(() => {
            delete pendingSavesRef.current[key];
            persistExerciseField(planId, dayIdx, exIdx, field, value, week);
            delete saveTimersRef.current[key];
        }, SAVE_DEBOUNCE_MS);
    }, [clearSaveTimer, persistExerciseField]);

    const queueDaySave = useCallback((planId, dayIdx, bodyData) => {
        if (isTemplatePlanId(planId)) return;
        const key = `day-${planId}-${dayIdx}-${Object.keys(bodyData).join('.')}`;
        clearSaveTimer(key);
        pendingSavesRef.current[key] = { kind: 'day', planId, dayIdx, bodyData };
        saveTimersRef.current[key] = setTimeout(() => {
            delete pendingSavesRef.current[key];
            persistDayField(planId, dayIdx, bodyData);
            delete saveTimersRef.current[key];
        }, SAVE_DEBOUNCE_MS);
    }, [clearSaveTimer, persistDayField]);

    useEffect(() => {
        const onLeave = () => flushPendingSaves();
        window.addEventListener('pagehide', onLeave);
        const onVis = () => {
            if (document.visibilityState === 'hidden') onLeave();
        };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            window.removeEventListener('pagehide', onLeave);
            document.removeEventListener('visibilitychange', onVis);
            flushPendingSaves();
        };
    }, [flushPendingSaves]);

    useEffect(() => {
        try { localStorage.setItem(LS_TAB, activeTab); } catch { /* ignore */ }
    }, [activeTab]);

    useEffect(() => {
        try { localStorage.setItem(LS_PLANS_SEARCH, plansSearch); } catch { /* ignore */ }
    }, [plansSearch]);

    useEffect(() => {
        try { localStorage.setItem(LS_GALLERY_SEARCH, gallerySearch); } catch { /* ignore */ }
    }, [gallerySearch]);

    useEffect(() => {
        if (activeTab === 'plans') fetchPlans();
        if (activeTab === 'log') fetchLogs();
        if (activeTab === 'routines') {
            api.get('/training/coach/clients')
                .then((r) => setTemplateClients(Array.isArray(r.data?.data) ? r.data.data : []))
                .catch(() => setTemplateClients([]));
        }
    }, [activeTab]);

    /** Handoff desde Asesorados → Editar rutina: preselecciona el cliente. */
    useEffect(() => {
        let handoffId = null;
        try {
            handoffId = sessionStorage.getItem('coach.planning.selectedUserId');
            if (handoffId) sessionStorage.removeItem('coach.planning.selectedUserId');
        } catch { /* ignore */ }
        if (!handoffId) return;
        setPlansSearch(String(handoffId));
        setActiveTab('plans');
    }, []);

    useEffect(() => {
        if (activeTab !== 'editor' || !editingPlan?.id) return;
        let active = true;
        api.get('/training/admin/gallery')
            .then((resp) => {
                if (active && resp.data?.success) setGalleryExercises(resp.data.data || []);
            })
            .catch(() => {});
        return () => { active = false; };
    }, [activeTab, editingPlan?.id]);

    const filteredPlans = useMemo(() => {
        const q = plansSearch.trim().toLowerCase();
        if (!q) return plans;
        return plans.filter((p) =>
            String(p.user_name || '').toLowerCase().includes(q)
            || String(p.user_id || '').includes(q)
            || String(p.level || '').toLowerCase().includes(q)
            || String(p.method || '').toLowerCase().includes(q)
            || String(p.id).includes(q),
        );
    }, [plans, plansSearch]);

    const filteredGallery = useMemo(() => {
        const q = gallerySearch.trim().toLowerCase();
        if (!q) return galleryExercises;
        return galleryExercises.filter((ex) =>
            String(ex.name || '').toLowerCase().includes(q)
            || String(ex.muscle_group || '').toLowerCase().includes(q),
        );
    }, [galleryExercises, gallerySearch]);

    const activeWeekStreak = useMemo(() => {
        if (!editingPlan?.user_id) {
            return { count: 0, last_activity_week: null, reset_pending: false };
        }
        const fechas = (logs || [])
            .filter((l) => Number(l.user_id) === Number(editingPlan.user_id))
            .map((l) => l.fecha)
            .filter(Boolean);
        return computeActiveWeekStreak(fechas, { resetAfterWeeks: 4 });
    }, [editingPlan?.user_id, logs]);

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const resp = await api.get('/training/admin/plans');
            if (resp.data.success) {
                setPlans(resp.data.data || []);
            }
        } catch {
            setError('Error al cargar planes');
        } finally {
            setLoading(false);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const resp = await api.get('/training/admin/log');
            if (resp.data.success) {
                setLogs(resp.data.data || []);
            }
        } catch {
            setError('Error al cargar diario');
        } finally {
            setLoading(false);
        }
    };

    const deletePlan = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este plan?')) return;
        try {
            await api.delete(`/training/admin/plans/${id}`);
            fetchPlans();
        } catch {
            alert('Error al eliminar');
        }
    };

    const startEditing = async (planId) => {
        try {
            const resp = await api.get(`/training/admin/plans/${planId}`);
            if (resp.data?.success && resp.data?.data) {
                setEditingPlan(resp.data.data);
                setActiveTab('editor');
                setProgramScope('base');
                setProgramWeek(isoWeekKey());
                return;
            }
            alert(resp.data?.error || 'Error cargando el plan para editar');
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Error cargando el plan para editar';
            alert(msg);
        }
    };

    const openTemplateEditor = (planLike) => {
        setEditingPlan(planLike);
        setActiveTab('editor');
        setProgramScope('base');
        setPreviewDayIdx(null);
        setTemplateAssignUserId('');
    };

    const createNewTemplate = () => {
        openTemplateEditor(emptyCoachTemplatePlan());
    };

    const saveTemplate = async ({ assignAfter = false } = {}) => {
        if (!editingPlan?.is_template) return;
        const nombre = String(editingPlan.nombre || '').trim();
        if (!nombre) {
            alert('Escribe un nombre para la rutina.');
            return;
        }
        const dias = (editingPlan.dias || []).map((d, i) => ({
            ...d,
            dia: i + 1,
            nombre: d.nombre || `Día ${i + 1}`,
            weekday: d.weekday || '',
            ejercicios: (d.ejercicios || []).filter((ex) => String(ex.exercise_name || '').trim()),
        }));
        const hasSport = dias.some((d) => normalizeSessionKind(d.session_kind) !== SESSION_KINDS.GYM);
        const hasGymWork = dias.some((d) => (d.ejercicios || []).length > 0);
        if (!hasSport && !hasGymWork) {
            alert('Agrega al menos un ejercicio o configura un día Deportes.');
            return;
        }
        setTemplateSaving(true);
        try {
            const body = {
                nombre,
                objetivo: editingPlan.method || 'hipertrofia',
                nivel: editingPlan.level || 'intermedio',
                dias,
            };
            let routineId = editingPlan.routine_id;
            if (routineId) {
                await api.put(`/training/coach/manual-routine/${routineId}`, body);
            } else {
                const created = await api.post('/training/coach/manual-routine', body);
                routineId = created.data?.data?.id;
                setEditingPlan((prev) => (prev ? {
                    ...prev,
                    routine_id: routineId,
                    id: `tpl:${routineId}`,
                } : prev));
            }
            setLibraryRefreshKey((k) => k + 1);
            if (assignAfter) {
                if (!templateAssignUserId || !routineId) {
                    alert('Guarda la rutina y elige un asesorado para asignar.');
                } else {
                    await api.post('/training/coach/ai-assign-plan', {
                        routine_id: Number(routineId),
                        user_id: Number(templateAssignUserId),
                        dias: Math.max(2, dias.length),
                    });
                    alert('Rutina guardada y asignada. Revisa «Rutinas asignadas».');
                    setActiveTab('plans');
                    setEditingPlan(null);
                    fetchPlans();
                    return;
                }
            } else {
                alert('Rutina guardada en tu biblioteca.');
            }
        } catch (err) {
            alert(err.response?.data?.error || 'No se pudo guardar la rutina');
        } finally {
            setTemplateSaving(false);
        }
    };

    const updateDay = (planId, dayIdx, fieldPath, value, { persist = true } = {}) => {
        const newPlan = { ...editingPlan };
        const day = newPlan.dias[dayIdx];
        let bodyData = {};

        if (fieldPath.startsWith('cardio.')) {
            const cardioField = fieldPath.split('.')[1];
            if (!day.cardio) day.cardio = {};
            day.cardio[cardioField] = value;
            bodyData = { cardio: day.cardio };
        } else {
            day[fieldPath] = value;
            bodyData = { [fieldPath]: value };
        }

        setEditingPlan(newPlan);
        if (persist) queueDaySave(planId, dayIdx, bodyData);
    };

    const setDaySessionSport = (planId, dayIdx, kind, prescription) => {
        let payload = null;
        setEditingPlan((prev) => {
            if (!prev?.dias?.[dayIdx]) return prev;
            const prevDay = prev.dias[dayIdx];
            const k = (kind != null && kind !== '')
                ? normalizeSessionKind(kind)
                : normalizeSessionKind(prevDay.session_kind);
            let rx;
            if (k === SESSION_KINDS.GYM) {
                rx = null;
            } else if (prescription === undefined) {
                const sameKind = normalizeSessionKind(prevDay.session_kind) === k;
                rx = sameKind && prevDay.sport_prescription
                    ? prevDay.sport_prescription
                    : emptySportPrescription(k);
            } else {
                rx = prescription;
            }
            payload = { session_kind: k, sport_prescription: rx };
            return {
                ...prev,
                dias: prev.dias.map((d, i) => (
                    i === dayIdx
                        ? { ...d, session_kind: k, sport_prescription: rx }
                        : d
                )),
            };
        });
        if (payload) {
            queueDaySave(planId, dayIdx, payload);
        }
    };

    const assignDaySport = async (planId, dayIdx) => {
        const day = editingPlan?.dias?.[dayIdx];
        if (!day) return;
        const k = normalizeSessionKind(day.session_kind);
        if (k === SESSION_KINDS.GYM) {
            setSportAssignMsg({ dayIdx, text: 'Elige Running o Natación antes de asignar.' });
            return;
        }
        if (isTemplatePlanId(planId)) {
            setSportAssignMsg({
                dayIdx,
                text: k === SESSION_KINDS.RUNNING
                    ? 'Deportes Running listo en la plantilla. Pulsa «Guardar rutina».'
                    : 'Deportes Natación listo en la plantilla. Pulsa «Guardar rutina».',
            });
            return;
        }
        const rx = day.sport_prescription || emptySportPrescription(k);
        const key = `day-${planId}-${dayIdx}-session_kind.sport_prescription`;
        clearSaveTimer(key);
        delete pendingSavesRef.current[key];
        setSportAssigningDay(dayIdx);
        setSportAssignMsg({ dayIdx, text: '' });
        try {
            await persistDayField(planId, dayIdx, {
                session_kind: k,
                sport_prescription: rx,
                coach_notes: day.coach_notes || '',
                technique_url: day.technique_url || '',
                weekday: day.weekday || '',
            });
            setSportAssignMsg({
                dayIdx,
                text: k === SESSION_KINDS.RUNNING
                    ? 'Entreno Running asignado. El cliente lo verá en su diario.'
                    : 'Entreno Natación asignado. El cliente lo verá en su diario.',
            });
        } catch {
            setSportAssignMsg({ dayIdx, text: 'No se pudo guardar. Reintenta.' });
        } finally {
            setSportAssigningDay(null);
        }
    };

    const applyGeneratedRunningWeek = async (dias) => {
        if (!editingPlan) return;
        const merged = appendGeneratedDays(editingPlan.dias || [], dias);
        const added = merged.length - (editingPlan.dias || []).length;
        const planId = editingPlan.id;
        if (isTemplatePlanId(planId)) {
            setEditingPlan((prev) => (prev ? { ...prev, dias: merged } : prev));
            setShowRunningWizard(false);
            alert(added
                ? `Se añadieron ${added} día(s) Running. Gimnasio y otros deportes no se tocaron.`
                : 'No había días nuevos para añadir.');
            return;
        }
        setApplyingRunningPlan(true);
        try {
            await api.put(`/training/admin/plans/${planId}`, { dias: merged });
            setShowRunningWizard(false);
            await startEditing(planId);
            alert(added
                ? `Se añadieron ${added} día(s) Running. Gimnasio y otros deportes no se tocaron.`
                : 'No había días nuevos para añadir.');
        } catch (err) {
            reportSaveError(err);
        } finally {
            setApplyingRunningPlan(false);
        }
    };

    const flushDayField = (planId, dayIdx, fieldPath, value) => {
        if (isTemplatePlanId(planId)) return;
        const day = editingPlan?.dias?.[dayIdx];
        if (!day) return;
        let bodyData = {};
        if (fieldPath.startsWith('cardio.')) {
            const cardioField = fieldPath.split('.')[1];
            bodyData = { cardio: { ...day.cardio, [cardioField]: value } };
        } else {
            bodyData = { [fieldPath]: value };
        }
        const key = `day-${planId}-${dayIdx}-${Object.keys(bodyData).join('.')}`;
        clearSaveTimer(key);
        delete pendingSavesRef.current[key];
        persistDayField(planId, dayIdx, bodyData);
    };

    const updateExercise = (planId, dayIdx, exIdx, field, value) => {
        const weekly = programScope === 'week' && WEEKLY_PROGRAM_FIELDS.includes(field);
        const newPlan = {
            ...editingPlan,
            dias: editingPlan.dias.map((d, i) => (
                i === dayIdx
                    ? { ...d, ejercicios: d.ejercicios.map((ex, j) => (j === exIdx ? { ...ex } : ex)) }
                    : d
            )),
            week_overrides: { ...(editingPlan.week_overrides || {}) },
        };
        if (weekly) {
            const w = programWeek;
            if (!newPlan.week_overrides[w]) newPlan.week_overrides[w] = {};
            const dKey = String(dayIdx);
            const eKey = String(exIdx);
            if (!newPlan.week_overrides[w][dKey]) newPlan.week_overrides[w][dKey] = {};
            newPlan.week_overrides[w][dKey][eKey] = {
                ...(newPlan.week_overrides[w][dKey][eKey] || {}),
                [field]: value,
            };
            setEditingPlan(newPlan);
            queueExerciseSave(planId, dayIdx, exIdx, field, value, w);
            return;
        }
        newPlan.dias[dayIdx].ejercicios[exIdx][field] = value;
        setEditingPlan(newPlan);
        queueExerciseSave(planId, dayIdx, exIdx, field, value, null);
    };

    const flushExerciseField = (planId, dayIdx, exIdx, field, value) => {
        if (isTemplatePlanId(planId)) return;
        const weekly = programScope === 'week' && WEEKLY_PROGRAM_FIELDS.includes(field);
        const week = weekly ? programWeek : null;
        const key = `ex-${planId}-${dayIdx}-${exIdx}-${field}-${week || 'base'}`;
        clearSaveTimer(key);
        delete pendingSavesRef.current[key];
        persistExerciseField(planId, dayIdx, exIdx, field, value, week);
    };

    const displayExercise = (dIdx, exIdx, ex) => (
        programScope === 'week'
            ? resolveExerciseForWeek(ex, editingPlan?.week_overrides, dIdx, exIdx, programWeek)
            : ex
    );

    const reorderExercise = async (planId, dayIdx, exIdx, direction) => {
        const toIdx = exIdx + direction;
        const day = editingPlan.dias[dayIdx];
        if (toIdx < 0 || toIdx >= day.ejercicios.length) return;

        if (isTemplatePlanId(planId)) {
            setEditingPlan((prev) => {
                if (!prev?.dias?.[dayIdx]) return prev;
                const ejercicios = [...prev.dias[dayIdx].ejercicios];
                const tmp = ejercicios[exIdx];
                ejercicios[exIdx] = ejercicios[toIdx];
                ejercicios[toIdx] = tmp;
                return {
                    ...prev,
                    dias: prev.dias.map((d, i) => (i === dayIdx ? { ...d, ejercicios } : d)),
                };
            });
            return;
        }

        try {
            await api.post(`/training/admin/plans/${planId}/reorder/${dayIdx}`, {
                fromIdx: exIdx,
                toIdx: toIdx
            });
            startEditing(planId);
        } catch {
            alert('Error reordenando');
        }
    };

    const addDay = async (planId, kind = SESSION_KINDS.GYM) => {
        const k = normalizeSessionKind(kind);
        const sport = k === SESSION_KINDS.GYM
            ? { session_kind: SESSION_KINDS.GYM, sport_prescription: null }
            : { session_kind: k, sport_prescription: emptySportPrescription(k) };
        const kindLabel = k === SESSION_KINDS.RUNNING
            ? 'Running'
            : (k === SESSION_KINDS.SWIMMING ? 'Natación' : '');
        if (isTemplatePlanId(planId)) {
            setEditingPlan((prev) => {
                if (!prev) return prev;
                const nextDia = (prev.dias?.length || 0) + 1;
                return {
                    ...prev,
                    dias: [
                        ...(prev.dias || []),
                        {
                            dia: nextDia,
                            nombre: kindLabel ? `Día ${nextDia} · ${kindLabel}` : `Día ${nextDia}`,
                            weekday: nextWeekday(prev.dias?.[prev.dias.length - 1]?.weekday),
                            completado: false,
                            session_kind: sport.session_kind,
                            sport_prescription: sport.sport_prescription,
                            warmup_url: '',
                            stretching_url: '',
                            technique_url: '',
                            coach_notes: '',
                            cardio: { goal: 'oxidación', bpm: 130, limit_type: 'time', limit_value: 20 },
                            ejercicios: [],
                        },
                    ],
                };
            });
            return;
        }
        try {
            await api.post(`/training/admin/plans/${planId}/add-day`, sport);
            startEditing(planId);
        } catch { alert('Error añadiendo día'); }
    };

    const deleteDay = async (planId, dayIdx) => {
        if (!confirm('¿Eliminar este día completo?')) return;
        if (isTemplatePlanId(planId)) {
            setEditingPlan((prev) => {
                if (!prev) return prev;
                const dias = (prev.dias || [])
                    .filter((_, i) => i !== dayIdx)
                    .map((d, i) => ({ ...d, dia: i + 1 }));
                return { ...prev, dias };
            });
            return;
        }
        try {
            await api.delete(`/training/admin/plans/${planId}/delete-day/${dayIdx}`);
            startEditing(planId);
        } catch { alert('Error eliminando día'); }
    };

    const addExercise = async (planId, dayIdx) => {
        if (isTemplatePlanId(planId)) {
            setEditingPlan((prev) => {
                if (!prev?.dias?.[dayIdx]) return prev;
                const ejercicios = [
                    ...(prev.dias[dayIdx].ejercicios || []),
                    {
                        exercise_name: '',
                        sets: 3,
                        reps: '10',
                        rest_seconds: 60,
                        intensity_type: 'RPE',
                        intensity_value: 8,
                        notes: '',
                        block_type: 'principal',
                        youtube_url: '',
                    },
                ];
                return {
                    ...prev,
                    dias: prev.dias.map((d, i) => (i === dayIdx ? { ...d, ejercicios } : d)),
                };
            });
            return;
        }
        try {
            await api.post(`/training/admin/plans/${planId}/add-exercise/${dayIdx}`);
            startEditing(planId);
        } catch { alert('Error añadiendo ejercicio'); }
    };

    const deleteExercise = async (planId, dayIdx, exIdx) => {
        if (!confirm('¿Eliminar este ejercicio?')) return;
        if (isTemplatePlanId(planId)) {
            setEditingPlan((prev) => {
                if (!prev?.dias?.[dayIdx]) return prev;
                const ejercicios = (prev.dias[dayIdx].ejercicios || []).filter((_, j) => j !== exIdx);
                return {
                    ...prev,
                    dias: prev.dias.map((d, i) => (i === dayIdx ? { ...d, ejercicios } : d)),
                };
            });
            return;
        }
        try {
            await api.delete(`/training/admin/plans/${planId}/delete-exercise/${dayIdx}/${exIdx}`);
            startEditing(planId);
        } catch { alert('Error eliminando ejercicio'); }
    };

    const duplicateDay = async (planId, dayIdx) => {
        if (!editingPlan?.dias?.[dayIdx] || duplicating) return;
        if (isTemplatePlanId(planId)) {
            setDuplicating(true);
            try {
                setEditingPlan((prev) => {
                    if (!prev?.dias?.[dayIdx]) return prev;
                    const source = prev.dias[dayIdx];
                    const copy = JSON.parse(JSON.stringify(source));
                    copy.dia = prev.dias.length + 1;
                    copy.nombre = `${source.nombre || `Día ${dayIdx + 1}`} (copia)`;
                    return { ...prev, dias: [...prev.dias, copy] };
                });
                setPreviewDayIdx((editingPlan.dias?.length) || 0);
            } finally {
                setDuplicating(false);
            }
            return;
        }
        const sourceDay = editingPlan.dias[dayIdx];
        setDuplicating(true);
        try {
            await api.post(`/training/admin/plans/${planId}/add-day`);
            let resp = await api.get(`/training/admin/plans/${planId}`);
            const plan = resp.data.data;
            const newDayIdx = plan.dias.length - 1;
            await api.patch(`/training/admin/plans/${planId}/day/${newDayIdx}`, {
                nombre: `${sourceDay.nombre} (copia)`,
                warmup_url: sourceDay.warmup_url || '',
                stretching_url: sourceDay.stretching_url || '',
                technique_url: sourceDay.technique_url || '',
                cardio: sourceDay.cardio || undefined,
            });
            for (const ex of sourceDay.ejercicios || []) {
                await api.post(`/training/admin/plans/${planId}/add-exercise/${newDayIdx}`);
                resp = await api.get(`/training/admin/plans/${planId}`);
                const exIdx = resp.data.data.dias[newDayIdx].ejercicios.length - 1;
                await api.patch(`/training/admin/plans/${planId}/exercise/${newDayIdx}/${exIdx}`, {
                    exercise_name: ex.exercise_name,
                    sets: ex.sets,
                    reps: ex.reps,
                    rest_seconds: ex.rest_seconds,
                    intensity_type: ex.intensity_type,
                    intensity_value: ex.intensity_value,
                    notes: ex.notes || '',
                    youtube_url: ex.youtube_url || '',
                });
            }
            await startEditing(planId);
            setPreviewDayIdx(newDayIdx);
        } catch {
            alert('Error duplicando día');
        } finally {
            setDuplicating(false);
        }
    };

    const addExerciseFromGallery = async (planId, dayIdx, galleryItem) => {
        if (!galleryItem) return;
        if (isTemplatePlanId(planId)) {
            setEditingPlan((prev) => {
                if (!prev?.dias?.[dayIdx]) return prev;
                const ejercicios = [
                    ...(prev.dias[dayIdx].ejercicios || []),
                    {
                        exercise_name: galleryItem.name,
                        youtube_url: galleryItem.youtube_url || '',
                        sets: 3,
                        reps: 10,
                        rest_seconds: 60,
                        intensity_type: 'RPE',
                        intensity_value: 8,
                        notes: '',
                        block_type: 'principal',
                    },
                ];
                return {
                    ...prev,
                    dias: prev.dias.map((d, i) => (i === dayIdx ? { ...d, ejercicios } : d)),
                };
            });
            return;
        }
        try {
            await api.post(`/training/admin/plans/${planId}/add-exercise/${dayIdx}`);
            const resp = await api.get(`/training/admin/plans/${planId}`);
            const exIdx = resp.data.data.dias[dayIdx].ejercicios.length - 1;
            await api.patch(`/training/admin/plans/${planId}/exercise/${dayIdx}/${exIdx}`, {
                exercise_name: galleryItem.name,
                youtube_url: galleryItem.youtube_url || '',
                sets: 3,
                reps: 10,
                rest_seconds: 60,
                intensity_type: 'RPE',
                intensity_value: 8,
            });
            await startEditing(planId);
        } catch {
            alert('Error agregando ejercicio desde galería');
        }
    };

    const previewDay = previewDayIdx != null ? editingPlan?.dias?.[previewDayIdx] : null;
    const isTemplateEditor = Boolean(editingPlan?.is_template);

    return (
        <div className="card training-coach-view training-light-island surface-readable d28d-admin-surface ui-light-surface max-w-7xl w-full mx-auto min-h-[80vh] overflow-x-hidden">
            {onBack && (
              <button type="button" className="btn-secondary text-sm mb-4" onClick={onBack}>
                ← Volver a Entrenadores
              </button>
            )}
            <h2 className="text-3xl font-bold text-stone-900 mb-2">
              {isCoachOnlyView ? t('coach.routines.title', 'Editar rutinas') : 'Rutinas'}
            </h2>
            <p className="text-stone-600 mb-6 font-medium">
              {isCoachOnlyView
                ? t('coach.routines.subtitle', 'Crea rutinas (biblioteca), asígnalas a tus clientes y edita las ya asignadas. En cada día puedes elegir Gimnasio o Deportes (Running / Natación).')
                : 'Asignación y edición de rutinas + diario de seguimiento por usuario.'}
            </p>

            <div className="flex gap-4 border-b border-stone-200 mb-6 flex-wrap">
                <button
                    type="button"
                    className={`pb-2 px-1 font-semibold ${activeTab === 'routines' ? 'border-b-2 border-lime-500 text-stone-900' : 'text-stone-500'}`}
                    onClick={() => setActiveTab('routines')}
                >
                    {t('coach.routines.library_tab', 'Rutinas')}
                </button>
                <button
                    type="button"
                    className={`pb-2 px-1 font-semibold ${activeTab === 'plans' ? 'border-b-2 border-lime-500 text-stone-900' : 'text-stone-500'}`}
                    onClick={() => setActiveTab('plans')}
                >
                    {isCoachOnlyView
                      ? t('coach.routines.assigned_heading', 'Rutinas asignadas')
                      : 'Planes Activos'}
                </button>
                <button
                    type="button"
                    className={`pb-2 px-1 font-semibold ${activeTab === 'log' ? 'border-b-2 border-lime-500 text-stone-900' : 'text-stone-500'}`}
                    onClick={() => setActiveTab('log')}
                >
                    Diario Global
                </button>
            </div>

            {error && <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-4 text-sm">{error}</div>}

            {activeTab === 'routines' && (
                <CoachRoutinesTab
                    refreshKey={libraryRefreshKey}
                    onCreateTemplate={createNewTemplate}
                    onEditTemplate={openTemplateEditor}
                />
            )}

            {/* TABS CONTENT */}
            {activeTab === 'plans' && (
                <div>
                    <div className="atm-plans-toolbar">
                        <h3 className="text-lg font-bold text-stone-900 m-0">
                          {isCoachOnlyView
                            ? t('coach.routines.assigned_heading', 'Rutinas asignadas')
                            : 'Planes Asignados'}
                        </h3>
                        <div className="atm-plans-toolbar__filters">
                            <input
                                type="search"
                                className="input text-sm atm-plans-toolbar__search"
                                placeholder={isCoachOnlyView
                                  ? t('coach.routines.search_ph', 'Buscar por cliente, ID o nivel…')
                                  : 'Buscar por usuario, ID o nivel…'}
                                value={plansSearch}
                                onChange={(e) => setPlansSearch(e.target.value)}
                            />
                            {!isCoachOnlyView && (
                              <button type="button" className="btn btn-primary text-sm" onClick={() => setShowGenerator(!showGenerator)}>
                                  {showGenerator ? 'Cerrar' : '+ Asignar nuevo plan'}
                              </button>
                            )}
                            {isCoachOnlyView && onNavigate && (
                              <button type="button" className="btn btn-secondary text-sm" onClick={() => onNavigate('coachai')}>
                                {t('coach.routines.go_ai', 'Ir a Asistente IA')}
                              </button>
                            )}
                        </div>
                    </div>

                    {!isCoachOnlyView && showGenerator && (
                        <div className="bg-stone-50 p-4 border border-stone-200 rounded-xl mb-6">
                            <h4 className="font-bold mb-3">Generar Plan Manual</h4>
                            <p className="text-sm text-stone-600 mb-4">Usa CURL con <code>/api/training/admin/plans</code> para pruebas rápidas de este MVP o usa el módulo normal de entrenamiento como usuario para auto-generarlo, luego los verás aquí para editarlos.</p>
                        </div>
                    )}

                    {loading ? <p className="text-stone-600">Cargando planes...</p> : (
                        <div className="atm-plans-table-wrap">
                            <table className="atm-plans-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Usuario</th>
                                        <th>
                                            {isCoachOnlyView ? 'Nivel' : 'Nivel/Método'}
                                        </th>
                                        <th>Días</th>
                                        <th>Actualizado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredPlans.map(p => (
                                        <tr key={p.id}>
                                            <td className="atm-plans-table__id">{p.id}</td>
                                            <td>
                                              <span className="atm-plans-table__user">{p.user_name}</span>
                                              <span className="atm-plans-table__sub">ID: {p.user_id}</span>
                                            </td>
                                            <td>
                                              <span className="atm-plans-badge">
                                                {formatPlanLevelCell(p, { coachView: isCoachOnlyView })}
                                              </span>
                                            </td>
                                            <td className="atm-plans-table__muted">{p.dias?.length || 0} días</td>
                                            <td className="atm-plans-table__muted">{new Date(p.updated_at).toLocaleDateString()}</td>
                                            <td className="atm-plans-table__actions">
                                              <div className="atm-plans-actions">
                                                <button
                                                  type="button"
                                                  className="atm-plans-btn atm-plans-btn--edit"
                                                  onClick={() => startEditing(p.id)}
                                                >
                                                  <Pencil className="w-3.5 h-3.5" aria-hidden />
                                                  Editar
                                                </button>
                                                <button
                                                  type="button"
                                                  className="atm-plans-btn atm-plans-btn--danger"
                                                  onClick={() => deletePlan(p.id)}
                                                >
                                                  <Trash2 className="w-3.5 h-3.5" aria-hidden />
                                                  Borrar
                                                </button>
                                              </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredPlans.length === 0 && !plans.length && isCoachOnlyView && (
                                        <tr>
                                          <td colSpan="6" className="atm-plans-table__empty">
                                            <div className="text-center max-w-md mx-auto space-y-4 py-4">
                                              <p className="font-semibold text-stone-800">
                                                {t('coach.routines.empty_title', 'Aún no hay rutinas asignadas')}
                                              </p>
                                              <p className="text-sm text-stone-600">
                                                {t('coach.routines.empty_body', 'Primero asigna una rutina desde Asistente IA (generar → elegir cliente). Después podrás editarla aquí.')}
                                              </p>
                                              {onNavigate && (
                                                <div className="flex flex-wrap justify-center gap-2">
                                                  <button type="button" className="btn btn-primary text-sm" onClick={() => onNavigate('coachai')}>
                                                    {t('coach.routines.go_ai', 'Ir a Asistente IA')}
                                                  </button>
                                                  <button type="button" className="btn btn-secondary text-sm" onClick={() => onNavigate('adminusers')}>
                                                    {t('coach.routines.go_clients', 'Ver mis usuarios')}
                                                  </button>
                                                  <button type="button" className="btn btn-secondary text-sm" onClick={() => onNavigate('training-coach-pay')}>
                                                    {t('coach.routines.go_activate', 'Activar clientes')}
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                    )}
                                    {filteredPlans.length === 0 && !(plans.length === 0 && isCoachOnlyView) && (
                                        <tr>
                                          <td colSpan="6" className="atm-plans-table__empty">
                                            {plans.length ? 'Sin coincidencias para la búsqueda.' : 'No hay planes creados/asignados aún.'}
                                          </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'editor' && (
                <div>
                    {!editingPlan ? (
                        <div className="text-center p-8 text-stone-500 border border-dashed rounded-xl">
                            {isCoachOnlyView
                              ? t('coach.routines.editor_hint', 'Elige «Editar» en Rutinas o en Rutinas asignadas.')
                              : 'Selecciona "Editar Rutina" desde la pestaña de Planes Activos.'}
                        </div>
                    ) : (
                        <div className="grid xl:grid-cols-[1fr_280px] gap-6">
                            <div>
                            <div className="flex items-center justify-between bg-stone-100 p-4 rounded-xl mb-4 gap-3 flex-wrap">
                                <div className="space-y-2 flex-1 min-w-[220px]">
                                    {isTemplateEditor ? (
                                      <>
                                        <h3 className="font-bold text-lg m-0">
                                          {editingPlan.routine_id ? `Editando rutina #${editingPlan.routine_id}` : 'Nueva rutina'}
                                        </h3>
                                        <input
                                          type="text"
                                          className="input w-full text-sm h-9 font-semibold"
                                          placeholder="Nombre de la rutina"
                                          value={editingPlan.nombre || ''}
                                          onChange={(e) => setEditingPlan((prev) => (prev ? { ...prev, nombre: e.target.value } : prev))}
                                        />
                                        <div className="flex flex-wrap gap-2">
                                          <select
                                            className="input text-sm h-9"
                                            value={editingPlan.level || 'intermedio'}
                                            onChange={(e) => setEditingPlan((prev) => (prev ? { ...prev, level: e.target.value } : prev))}
                                          >
                                            <option value="principiante">Principiante</option>
                                            <option value="intermedio">Intermedio</option>
                                            <option value="avanzado">Avanzado</option>
                                          </select>
                                          <select
                                            className="input text-sm h-9"
                                            value={editingPlan.method || 'hipertrofia'}
                                            onChange={(e) => setEditingPlan((prev) => (prev ? { ...prev, method: e.target.value } : prev))}
                                          >
                                            <option value="hipertrofia">Hipertrofia</option>
                                            <option value="fuerza">Fuerza</option>
                                            <option value="resistencia">Resistencia</option>
                                            <option value="mantenimiento">Mantenimiento</option>
                                          </select>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <h3 className="font-bold text-lg">Editando Plan #{editingPlan.id}</h3>
                                        <p className="text-sm text-stone-600">
                                            Usuario ID: {editingPlan.user_id}
                                            {' · '}
                                            {formatPlanEditorMeta(editingPlan, { coachView: isCoachOnlyView })}
                                        </p>
                                      </>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className="btn-secondary text-sm"
                                    onClick={() => setShowRunningWizard(true)}
                                  >
                                    Añadir días Running
                                  </button>
                                  {isTemplateEditor ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn-primary text-sm"
                                        disabled={templateSaving}
                                        onClick={() => saveTemplate()}
                                      >
                                        {templateSaving ? 'Guardando…' : 'Guardar rutina'}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-secondary text-sm"
                                        onClick={() => { setActiveTab('routines'); setPreviewDayIdx(null); }}
                                      >
                                        Volver a Rutinas
                                      </button>
                                    </>
                                  ) : (
                                    <button type="button" className="btn-secondary text-sm" onClick={() => { setActiveTab('plans'); setPreviewDayIdx(null); }}>Volver a planes</button>
                                  )}
                                </div>
                            </div>

                            {isTemplateEditor ? (
                              <div className="mb-6 p-4 rounded-xl border border-stone-200 bg-white space-y-3">
                                <p className="text-xs text-stone-600 m-0">
                                  Edita días y ejercicios como en una rutina asignada. Al guardar queda en tu biblioteca; luego puedes asignarla a un asesorado.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                                  <label className="block flex-1">
                                    <span className="text-xs font-bold uppercase text-stone-500">Asignar al guardar (opcional)</span>
                                    <select
                                      className="input mt-1 w-full text-sm"
                                      value={templateAssignUserId}
                                      onChange={(e) => setTemplateAssignUserId(e.target.value)}
                                    >
                                      <option value="">Solo guardar en biblioteca</option>
                                      {templateClients.map((c) => (
                                        <option key={c.id} value={c.id}>
                                          {c.nombre || c.name || c.email}
                                          {c.email ? ` (${c.email})` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <button
                                    type="button"
                                    className="btn-primary text-sm"
                                    disabled={templateSaving || !templateAssignUserId}
                                    onClick={() => saveTemplate({ assignAfter: true })}
                                  >
                                    Guardar y asignar
                                  </button>
                                </div>
                              </div>
                            ) : (
                            <div className="mb-6 p-4 rounded-xl border border-stone-200 bg-white space-y-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-bold uppercase tracking-wide text-stone-500">Programar</span>
                                    <button
                                        type="button"
                                        className={programScope === 'base' ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                                        onClick={() => setProgramScope('base')}
                                    >
                                        Plantilla base
                                    </button>
                                    <button
                                        type="button"
                                        className={programScope === 'week' ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
                                        onClick={() => setProgramScope('week')}
                                    >
                                        Semana ISO
                                    </button>
                                    {programScope === 'week' ? (
                                        <div className="flex items-center gap-2 ml-1 flex-wrap">
                                            <button type="button" className="btn-secondary text-xs px-2" onClick={() => setProgramWeek((w) => shiftIsoWeekKey(w, -1))}>←</button>
                                            <span className="text-sm font-semibold text-stone-800 min-w-[5.5rem] text-center">{programWeek}</span>
                                            <button type="button" className="btn-secondary text-xs px-2" onClick={() => setProgramWeek((w) => shiftIsoWeekKey(w, 1))}>→</button>
                                            <button type="button" className="btn-secondary text-xs" onClick={() => setProgramWeek(isoWeekKey())}>Hoy</button>
                                            <span
                                                className="text-xs font-semibold text-stone-800 bg-lime-50 border border-lime-200 rounded-lg px-2.5 py-1"
                                                title={activeWeekStreak.reset_pending
                                                    ? 'Más de 4 semanas sin registro: el contador se reinicia al próximo diario'
                                                    : 'Semanas con registro desde el último reinicio (>4 sem inactivo)'}
                                            >
                                                Semana activa: {activeWeekStreak.count}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                                <p className="text-xs text-stone-600 m-0">
                                    {programScope === 'week'
                                        ? 'Esta semana: edita ejercicio, series, reps e intensidad (valores distintos a la plantilla y al diario del usuario). Descanso, notas, cardio y videos siguen en la plantilla base.'
                                        : 'Plantilla base: cambios permanentes del día (incluye descanso, notas, cardio y videos). El diario del usuario (lo que registra) no se sobrescribe.'}
                                </p>
                            </div>
                            )}

                            <div className="space-y-8">
                                {editingPlan.dias.map((d, dIdx) => (
                                    <div key={d.dia} className={`border rounded-2xl overflow-hidden shadow-sm ${previewDayIdx === dIdx ? 'border-lime-400 ring-2 ring-lime-200' : 'border-stone-200'}`}>
                                        <div className="bg-stone-50 border-b border-stone-200 p-4 flex flex-col gap-3">
                                            <div className="flex justify-between items-start flex-wrap gap-2">
                                                <div className="flex-1 min-w-[220px] space-y-1">
                                                    <label className="text-xs font-bold text-stone-600">
                                                        Título del día (editable)
                                                    </label>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-semibold text-stone-500 whitespace-nowrap">Día {d.dia} ·</span>
                                                        <input
                                                            type="text"
                                                            className="input flex-1 text-sm h-9 font-bold text-stone-900"
                                                            value={d.nombre || ''}
                                                            placeholder={`Ej: Torso A / Pierna`}
                                                            onChange={(e) => updateDay(editingPlan.id, dIdx, 'nombre', e.target.value)}
                                                            onBlur={(e) => flushDayField(editingPlan.id, dIdx, 'nombre', e.target.value)}
                                                        />
                                                    </div>
                                                    <label className="block">
                                                        <span className="text-xs font-bold text-stone-600">Día de la semana</span>
                                                        <select
                                                            className="input mt-1 w-full text-sm h-9"
                                                            value={d.weekday || ''}
                                                            onChange={(e) => {
                                                                updateDay(editingPlan.id, dIdx, 'weekday', e.target.value);
                                                                flushDayField(editingPlan.id, dIdx, 'weekday', e.target.value);
                                                            }}
                                                        >
                                                            <option value="">Sin asignar</option>
                                                            {WEEKDAYS.map((w) => (
                                                                <option key={w.id} value={w.id}>{w.label}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <button
                                                        type="button"
                                                        className="text-xs font-semibold text-lime-800 hover:underline"
                                                        onClick={() => setPreviewDayIdx(dIdx)}
                                                    >
                                                        Vista previa
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="text-xs font-semibold text-stone-700 hover:underline"
                                                        disabled={duplicating}
                                                        onClick={() => duplicateDay(editingPlan.id, dIdx)}
                                                    >
                                                        {duplicating ? 'Copiando…' : 'Duplicar día'}
                                                    </button>
                                                    <span className="text-xs font-semibold bg-stone-200 text-stone-700 px-2 py-1 rounded-full">{d.ejercicios?.length} ejercicios</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteDay(editingPlan.id, dIdx)}
                                                        className="training-day-delete-btn"
                                                    >
                                                        Eliminar Día
                                                    </button>
                                                </div>
                                            </div>

                                            <SportDayPrescriptionEditor
                                                sessionKind={d.session_kind || SESSION_KINDS.GYM}
                                                prescription={d.sport_prescription}
                                                coachNotes={d.coach_notes || ''}
                                                techniqueUrl={d.technique_url || ''}
                                                onKindChange={(k) => setDaySessionSport(
                                                    editingPlan.id,
                                                    dIdx,
                                                    k,
                                                    undefined,
                                                )}
                                                onPrescriptionChange={(rx) => setDaySessionSport(
                                                    editingPlan.id,
                                                    dIdx,
                                                    null,
                                                    rx,
                                                )}
                                                onCoachNotesChange={(text) => updateDay(editingPlan.id, dIdx, 'coach_notes', text)}
                                                onCoachNotesBlur={(text) => flushDayField(editingPlan.id, dIdx, 'coach_notes', text)}
                                                onTechniqueUrlChange={(url) => updateDay(editingPlan.id, dIdx, 'technique_url', url, { persist: false })}
                                                onTechniqueUrlBlur={(url) => flushDayField(editingPlan.id, dIdx, 'technique_url', url)}
                                                onAssignSport={() => assignDaySport(editingPlan.id, dIdx)}
                                                assigning={sportAssigningDay === dIdx}
                                                assignMessage={sportAssignMsg.dayIdx === dIdx ? sportAssignMsg.text : ''}
                                            />
                                            <p className="text-[11px] text-stone-500 m-0">
                                              {t(
                                                'coach.routines.sport_hint',
                                                'Cada día es independiente: puedes dejar gimnasio, pasar a Running/Natación o añadir ejercicios de gym en un día de deporte. Añadir días no borra los que ya existen.',
                                              )}
                                            </p>

                                            </div>

                                            <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 p-3 bg-white border border-stone-200 rounded-xl">
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="text-xs font-bold text-stone-600">Video Calentamiento</label>
                                                        <input
                                                            type="text"
                                                            className="input w-full mt-1 text-sm h-8"
                                                            value={d.warmup_url || ''}
                                                            placeholder="Ej: https://youtube.com/watch?v=..."
                                                            onChange={(e) => updateDay(editingPlan.id, dIdx, 'warmup_url', e.target.value)}
                                                            onBlur={(e) => flushDayField(editingPlan.id, dIdx, 'warmup_url', e.target.value)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-stone-600">Video Estiramiento</label>
                                                        <input
                                                            type="text"
                                                            className="input w-full mt-1 text-sm h-8"
                                                            value={d.stretching_url || ''}
                                                            placeholder="Ej: https://youtube.com/watch?v=..."
                                                            onChange={(e) => updateDay(editingPlan.id, dIdx, 'stretching_url', e.target.value)}
                                                            onBlur={(e) => flushDayField(editingPlan.id, dIdx, 'stretching_url', e.target.value)}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-bold text-stone-600">Video Técnica avanzada</label>
                                                        <input
                                                            type="text"
                                                            className="input w-full mt-1 text-sm h-8"
                                                            value={d.technique_url || ''}
                                                            placeholder="Ej: https://youtube.com/watch?v=..."
                                                            onChange={(e) => updateDay(editingPlan.id, dIdx, 'technique_url', e.target.value)}
                                                            onBlur={(e) => flushDayField(editingPlan.id, dIdx, 'technique_url', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="space-y-2 border-l border-stone-100 pl-4">
                                                    <label className="text-xs font-bold text-stone-600 block mb-1">Configuración de Cardio</label>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="text-xs text-stone-500">Objetivo</label>
                                                            <input type="text" className="input w-full text-sm h-8" value={d.cardio?.goal || 'oxidación'} onChange={(e) => updateDay(editingPlan.id, dIdx, 'cardio.goal', e.target.value)} />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-stone-500">Pulsaciones (PPM)</label>
                                                            <input type="number" className="input w-full text-sm h-8" value={d.cardio?.bpm || 130} onChange={(e) => updateDay(editingPlan.id, dIdx, 'cardio.bpm', Number(e.target.value))} />
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-stone-500">Límite</label>
                                                            <select className="input w-full text-sm h-8 py-0" value={d.cardio?.limit_type || 'time'} onChange={(e) => updateDay(editingPlan.id, dIdx, 'cardio.limit_type', e.target.value)}>
                                                                <option value="time">Tiempo (min)</option>
                                                                <option value="distance">Distancia (km)</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="text-xs text-stone-500">Minutos / Km</label>
                                                            <input type="number" className="input w-full text-sm h-8" value={d.cardio?.limit_value || 20} onChange={(e) => updateDay(editingPlan.id, dIdx, 'cardio.limit_value', Number(e.target.value))} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        <div className="bg-stone-50 p-2 border-b border-stone-100 flex justify-between items-center gap-2 flex-wrap">
                                            <button
                                                type="button"
                                                onClick={() => setPickerDayIdx(pickerDayIdx === dIdx ? null : dIdx)}
                                                className="text-xs bg-stone-200 text-stone-800 px-3 py-1 rounded-lg font-bold hover:bg-stone-300"
                                            >
                                                {pickerDayIdx === dIdx ? 'Cerrar galería' : '+ Desde galería'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => addExercise(editingPlan.id, dIdx)}
                                                className="text-xs bg-lime-500 text-white px-3 py-1 rounded-lg font-bold hover:bg-lime-600 transition-all"
                                            >
                                                + Añadir ejercicio vacío
                                            </button>
                                        </div>
                                        {pickerDayIdx === dIdx && (
                                            <div className="p-3 bg-lime-50 border-b border-lime-100 space-y-2">
                                                <input
                                                    type="search"
                                                    className="input w-full text-sm"
                                                    placeholder="Buscar en galería…"
                                                    value={gallerySearch}
                                                    onChange={(e) => setGallerySearch(e.target.value)}
                                                />
                                                <div className="max-h-40 overflow-y-auto grid sm:grid-cols-2 gap-1">
                                                    {filteredGallery.slice(0, 24).map((ex) => (
                                                        <button
                                                            key={ex.id}
                                                            type="button"
                                                            className="text-left text-xs px-2 py-1.5 rounded-lg bg-white border border-stone-200 hover:border-lime-400 truncate"
                                                            onClick={() => addExerciseFromGallery(editingPlan.id, dIdx, ex)}
                                                        >
                                                            {ex.name}
                                                            {ex.muscle_group ? ` · ${ex.muscle_group}` : ''}
                                                        </button>
                                                    ))}
                                                    {filteredGallery.length === 0 && (
                                                        <p className="text-xs text-stone-500 col-span-2">Sin ejercicios en galería. Agrega videos en Galería primero.</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <div className="p-0 overflow-x-auto">
                                            <table className="w-full text-left text-sm border-collapse min-w-[800px]">
                                                <thead>
                                                    <tr className="bg-stone-100 text-stone-600 text-xs">
                                                        <th className="p-3 w-10 text-center">Orden</th>
                                                        <th className="p-3">Ejercicio</th>
                                                        <th className="p-3 w-24">Series</th>
                                                        <th className="p-3 w-28">Reps</th>
                                                        <th className="p-3 w-32">Intensidad</th>
                                                        <th className="p-3 w-28">Descanso (s)</th>
                                                        <th className="p-3 w-32">Notas</th>
                                                        <th className="p-3 w-10"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {d.ejercicios.map((ex, exIdx) => {
                                                        const viewEx = displayExercise(dIdx, exIdx, ex);
                                                        return (
                                                        <tr key={`${dIdx}-${exIdx}`} className="border-b border-stone-100 hover:bg-stone-50">
                                                            <td className="p-2 text-center">
                                                                <div className="flex flex-col items-center gap-1">
                                                                    <button onClick={() => reorderExercise(editingPlan.id, dIdx, exIdx, -1)} disabled={exIdx === 0} className="text-stone-400 hover:text-stone-900 disabled:opacity-30">▲</button>
                                                                    <button onClick={() => reorderExercise(editingPlan.id, dIdx, exIdx, 1)} disabled={exIdx === d.ejercicios.length - 1} className="text-stone-400 hover:text-stone-900 disabled:opacity-30">▼</button>
                                                                </div>
                                                            </td>
                                                            <td className="p-2 font-semibold">
                                                                <input
                                                                    className="training-plan-field w-full bg-white text-stone-900 border border-stone-200 hover:border-stone-300 focus:border-lime-400 px-1 py-1 rounded outline-none w-[200px]"
                                                                    value={viewEx.exercise_name || ''}
                                                                    onChange={(e) => updateExercise(editingPlan.id, dIdx, exIdx, 'exercise_name', e.target.value)}
                                                                    onBlur={(e) => flushExerciseField(editingPlan.id, dIdx, exIdx, 'exercise_name', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="p-2">
                                                                <input
                                                                    type="number" min="1" max="10"
                                                                    className="w-16 input training-plan-field bg-white text-stone-900 text-center py-1 px-2 h-8"
                                                                    value={viewEx.sets ?? ''}
                                                                    onChange={(e) => updateExercise(editingPlan.id, dIdx, exIdx, 'sets', Number(e.target.value))}
                                                                    onBlur={(e) => flushExerciseField(editingPlan.id, dIdx, exIdx, 'sets', Number(e.target.value))}
                                                                />
                                                            </td>
                                                            <td className="p-2">
                                                                <input
                                                                    className="w-full input training-plan-field bg-white text-stone-900 py-1 px-2 h-8"
                                                                    value={viewEx.reps ?? ''}
                                                                    onChange={(e) => updateExercise(editingPlan.id, dIdx, exIdx, 'reps', e.target.value)}
                                                                    onBlur={(e) => flushExerciseField(editingPlan.id, dIdx, exIdx, 'reps', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="p-2">
                                                                <div className="flex gap-1 items-center">
                                                                    <select
                                                                        className="input training-plan-field bg-white text-stone-900 py-1 px-2 h-8 w-20 text-xs"
                                                                        value={viewEx.intensity_type || 'RPE'}
                                                                        onChange={(e) => updateExercise(editingPlan.id, dIdx, exIdx, 'intensity_type', e.target.value)}
                                                                        onBlur={(e) => flushExerciseField(editingPlan.id, dIdx, exIdx, 'intensity_type', e.target.value)}
                                                                    >
                                                                        <option value="RPE">RPE</option>
                                                                        <option value="RIR">RIR</option>
                                                                    </select>
                                                                    <input
                                                                        type="number" min="0" max="10" step="0.5"
                                                                        className="w-12 input training-plan-field bg-white text-stone-900 py-1 px-1 h-8 text-center text-xs"
                                                                        value={viewEx.intensity_value ?? ''}
                                                                        onChange={(e) => updateExercise(editingPlan.id, dIdx, exIdx, 'intensity_value', Number(e.target.value))}
                                                                        onBlur={(e) => flushExerciseField(editingPlan.id, dIdx, exIdx, 'intensity_value', Number(e.target.value))}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td className="p-2">
                                                                <input
                                                                    type="number" step="15"
                                                                    className="w-20 input training-plan-field bg-white text-stone-900 text-center py-1 px-2 h-8"
                                                                    value={ex.rest_seconds}
                                                                    onChange={(e) => updateExercise(editingPlan.id, dIdx, exIdx, 'rest_seconds', Number(e.target.value))}
                                                                    onBlur={(e) => flushExerciseField(editingPlan.id, dIdx, exIdx, 'rest_seconds', Number(e.target.value))}
                                                                    title={programScope === 'week' ? 'Descanso se edita en plantilla base' : undefined}
                                                                />
                                                            </td>
                                                            <td className="p-2">
                                                                <input
                                                                    className="w-full input training-plan-field bg-white text-stone-900 py-1 px-2 h-8 text-xs"
                                                                    placeholder="Notas..."
                                                                    value={ex.notes || ''}
                                                                    onChange={(e) => updateExercise(editingPlan.id, dIdx, exIdx, 'notes', e.target.value)}
                                                                    onBlur={(e) => flushExerciseField(editingPlan.id, dIdx, exIdx, 'notes', e.target.value)}
                                                                    title={programScope === 'week' ? 'Notas se editan en plantilla base' : undefined}
                                                                />
                                                            </td>
                                                            <td className="p-2">
                                                                <button onClick={() => deleteExercise(editingPlan.id, dIdx, exIdx)} className="text-red-400 hover:text-red-600 font-bold">×</button>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                            </>
                                    </div>
                                ))}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => addDay(editingPlan.id, SESSION_KINDS.GYM)}
                                        className="py-4 border-2 border-dashed border-stone-300 rounded-2xl text-stone-600 font-bold hover:bg-stone-50 hover:border-lime-400 transition-all"
                                    >
                                        + Día gimnasio
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => addDay(editingPlan.id, SESSION_KINDS.RUNNING)}
                                        className="py-4 border-2 border-dashed border-emerald-200 rounded-2xl text-emerald-800 font-bold hover:bg-emerald-50 hover:border-emerald-400 transition-all"
                                    >
                                        + Día Running
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => addDay(editingPlan.id, SESSION_KINDS.SWIMMING)}
                                        className="py-4 border-2 border-dashed border-sky-200 rounded-2xl text-sky-800 font-bold hover:bg-sky-50 hover:border-sky-400 transition-all"
                                    >
                                        + Día Natación
                                    </button>
                                </div>
                            </div>
                            </div>

                            <aside className="hidden xl:block">
                                <div className="card p-4 sticky top-4 space-y-3">
                                    <h4 className="font-bold text-sm uppercase tracking-wide text-stone-600">Vista previa del día</h4>
                                    {previewDay ? (
                                        <>
                                            <p className="font-semibold text-stone-900">{previewDay.nombre}</p>
                                            {weekdayLabel(previewDay.weekday) ? (
                                              <p className="text-xs text-stone-600 m-0">{weekdayLabel(previewDay.weekday)}</p>
                                            ) : null}
                                            {normalizeSessionKind(previewDay.session_kind) !== SESSION_KINDS.GYM ? (
                                              <div className="text-sm text-stone-700 space-y-1">
                                                <p className="text-xs font-bold text-emerald-800 m-0">
                                                  {normalizeSessionKind(previewDay.session_kind) === SESSION_KINDS.RUNNING
                                                    ? 'Deportes · Running'
                                                    : 'Deportes · Natación'}
                                                </p>
                                                {previewDay.sport_prescription?.zone_target != null ? (
                                                  <p className="m-0 text-xs">Zona {previewDay.sport_prescription.zone_target}</p>
                                                ) : null}
                                                {previewDay.sport_prescription?.distance_target_km != null ? (
                                                  <p className="m-0 text-xs">Meta {previewDay.sport_prescription.distance_target_km} km</p>
                                                ) : null}
                                                {previewDay.sport_prescription?.pool_length_m != null ? (
                                                  <p className="m-0 text-xs">Piscina {previewDay.sport_prescription.pool_length_m} m</p>
                                                ) : null}
                                                {String(previewDay.coach_notes || '').trim() ? (
                                                  <p className="m-0 text-xs rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-stone-800">
                                                    <span className="font-bold text-amber-900">Nota del coach: </span>
                                                    {String(previewDay.coach_notes).trim()}
                                                  </p>
                                                ) : null}
                                                {previewDay.technique_url ? (
                                                  <a href={previewDay.technique_url} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-700 hover:underline block">
                                                    Video de técnica →
                                                  </a>
                                                ) : (
                                                  <p className="text-xs text-stone-400 m-0">Sin video de técnica (opcional)</p>
                                                )}
                                                <p className="text-xs text-stone-500 m-0 pt-2">
                                                  El cliente registra métricas en su diario (tiempo digitado).
                                                </p>
                                              </div>
                                            ) : null}
                                            {(previewDay.ejercicios || []).length > 0 || normalizeSessionKind(previewDay.session_kind) === SESSION_KINDS.GYM ? (
                                              <>
                                            <p className="text-xs text-stone-500">{previewDay.ejercicios?.length || 0} ejercicios</p>
                                            <ul className="text-sm space-y-1 max-h-[50vh] overflow-y-auto">
                                                {(previewDay.ejercicios || []).map((ex, i) => (
                                                    <li key={i} className="text-stone-700 border-b border-stone-100 pb-1">
                                                        {ex.exercise_name}
                                                        <span className="text-xs text-stone-500 block">{ex.sets}×{ex.reps}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            {previewDay.technique_url && (
                                                <a href={previewDay.technique_url} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-700 hover:underline block">
                                                    Video técnica avanzada →
                                                </a>
                                            )}
                                            {previewDay.warmup_url && (
                                                <a href={previewDay.warmup_url} target="_blank" rel="noopener noreferrer" className="text-xs text-lime-700 hover:underline block">
                                                    Ver calentamiento
                                                </a>
                                            )}
                                              </>
                                            ) : null}
                                        </>
                                    ) : (
                                        <p className="text-sm text-stone-500">Selecciona «Vista previa» en un día para ver el resumen lateral.</p>
                                    )}
                                </div>
                            </aside>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'log' && (
                <div>
                    <h3 className="font-bold text-lg mb-1">Sesiones Reportadas (Diario)</h3>
                    <p className="text-sm text-stone-500 mb-4">Última sesión de cada cliente. El histórico queda debajo de cada tarjeta.</p>
                    {loading ? <p>Cargando diario...</p> : (
                        <div className="space-y-4">
                            {diaryByUser.latest.map((log) => {
                                const uid = Number(log.user_id);
                                const older = diaryByUser.historyByUser.get(uid) || [];
                                const open = Boolean(diaryHistoryOpen[uid]);
                                return (
                                    <div key={`latest-${log.id}`}>
                                        <DiaryLogCard log={log} />
                                        {older.length ? (
                                            <div className="mt-2 ml-1">
                                                <button
                                                    type="button"
                                                    className="text-sm font-semibold text-lime-700 hover:underline"
                                                    onClick={() => setDiaryHistoryOpen((prev) => ({ ...prev, [uid]: !prev[uid] }))}
                                                >
                                                    {open ? 'Ocultar anteriores' : `Ver ${older.length} sesión${older.length === 1 ? '' : 'es'} anterior${older.length === 1 ? '' : 'es'}`}
                                                </button>
                                                {open ? (
                                                    <div className="mt-3 space-y-3">
                                                        {older.map((hist) => <DiaryLogCard key={hist.id} log={hist} />)}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                            {diaryByUser.latest.length === 0 && (
                                <div className="text-center p-8 text-stone-500 border border-dashed rounded-xl">
                                    Aún no hay sesiones registradas en el diario por los usuarios.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'stats' && (
                <div className="space-y-6">
                    <h3 className="font-bold text-2xl text-stone-900 mb-2">Dashboard de Evolución del Atleta</h3>
                    <p className="text-stone-600 text-sm">Visualización de métricas de rendimiento y factores de recuperación agregados.</p>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="card border !border-stone-200 p-5 bg-stone-50">
                            <span className="text-xs font-bold text-stone-500 uppercase">Total Sesiones</span>
                            <p className="text-3xl font-black text-stone-900">{logs.length}</p>
                        </div>
                        <div className="card border !border-stone-200 p-5 bg-stone-50">
                            <span className="text-xs font-bold text-stone-500 uppercase">Promedio Energía</span>
                            <p className="text-3xl font-black text-lime-600">
                                {(logs.reduce((acc, l) => acc + (l.wellness?.energy_level || 0), 0) / (logs.length || 1)).toFixed(1)}/10
                            </p>
                        </div>
                        <div className="card border !border-stone-200 p-5 bg-stone-50">
                            <span className="text-xs font-bold text-stone-500 uppercase">Calidad Sueño Avg</span>
                            <p className="text-3xl font-black text-blue-600">
                                {(logs.reduce((acc, l) => acc + (l.wellness?.sleep_quality || 0), 0) / (logs.length || 1)).toFixed(1)}/10
                            </p>
                        </div>
                        <div className="card border !border-stone-200 p-5 bg-stone-50">
                            <span className="text-xs font-bold text-stone-500 uppercase">Estrés Promedio</span>
                            <p className="text-3xl font-black text-red-600">
                                {(logs.reduce((acc, l) => acc + (l.wellness?.stress_level || 0), 0) / (logs.length || 1)).toFixed(1)}/10
                            </p>
                        </div>
                    </div>

                    <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
                        <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                            <span className="p-2 bg-lime-100 text-lime-700 rounded-lg">📈</span>
                            Tendencia de Cargas por Usuario
                        </h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="text-xs font-bold text-stone-400 uppercase border-b border-stone-100">
                                        <th className="pb-3">Usuario</th>
                                        <th className="pb-3">Último Entrenamiento</th>
                                        <th className="pb-3 text-center">Ejercicios Totales</th>
                                        <th className="pb-3 text-center">Horas Sueño Avg</th>
                                        <th className="pb-3">Estado Sugerido</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-50">
                                    {Array.from(new Set(logs.map(l => l.user_id))).map(uid => {
                                        const userLogs = logs.filter(l => l.user_id === uid);
                                        const lastLog = userLogs[userLogs.length - 1];
                                        const avgSleep = userLogs.reduce((acc, l) => acc + (l.wellness?.sleep_hours || 0), 0) / userLogs.length;
                                        const avgStress = userLogs.reduce((acc, l) => acc + (l.wellness?.stress_level || 0), 0) / userLogs.length;
                                        
                                        return (
                                            <tr key={uid} className="text-sm hover:bg-stone-50">
                                                <td className="py-4 font-bold text-stone-800">{lastLog.user_name}</td>
                                                <td className="py-4 text-stone-600">{lastLog.fecha}</td>
                                                <td className="py-4 text-center font-semibold">{userLogs.reduce((acc, l) => acc + (l.ejercicios?.length || 0), 0)}</td>
                                                <td className="py-4 text-center font-bold text-blue-500">{avgSleep.toFixed(1)}h</td>
                                                <td className="py-4">
                                                    {avgStress > 7 ? (
                                                        <span className="text-[10px] bg-red-100 text-red-700 px-2 py-1 rounded-full font-bold">SOBRECARGADO</span>
                                                    ) : avgSleep < 6 ? (
                                                        <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold">DÉFICIT RECUPERACIÓN</span>
                                                    ) : (
                                                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold">OPTIMO PARA CARGA</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            <RunningPlanWizard
                open={showRunningWizard}
                onClose={() => setShowRunningWizard(false)}
                applying={applyingRunningPlan}
                onApply={applyGeneratedRunningWeek}
            />
        </div>
    );
}
